import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type JuiceEvent =
  | 'spin'
  | 'tick'
  | 'cash'
  | 'hit'
  | 'miss'
  | 'bankrupt'
  | 'lose'
  | 'solve'
  | 'win'
  | 'click';

interface JuiceContextType {
  muted: boolean;
  audioReady: boolean;
  toggleMuted: () => void;
  play: (event: JuiceEvent) => void;
}

const JuiceContext = createContext<JuiceContextType>({
  muted: false,
  audioReady: false,
  toggleMuted: () => undefined,
  play: () => undefined,
});

const STORAGE_KEY = 'spin-solve-muted';

type AudioBag = {
  ctx: AudioContext;
  master: GainNode;
};

function getBag(): AudioBag | null {
  if (typeof window === 'undefined') return null;
  const host = window as unknown as {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
    __spinSolveBag?: AudioBag;
  };
  if (host.__spinSolveBag) return host.__spinSolveBag;
  const Ctor = host.AudioContext || host.webkitAudioContext;
  if (!Ctor) return null;
  try {
    const ctx = new Ctor();
    const master = ctx.createGain();
    master.gain.value = 1;
    master.connect(ctx.destination);
    host.__spinSolveBag = { ctx, master };
    return host.__spinSolveBag;
  } catch {
    return null;
  }
}

function applyMuteToMaster(muted: boolean) {
  const bag = getBag();
  if (!bag) return;
  const { ctx, master } = bag;
  try {
    master.gain.cancelScheduledValues(ctx.currentTime);
    master.gain.setValueAtTime(muted ? 0.0001 : 1, ctx.currentTime);
  } catch {
    // ignore
  }
}

async function unlockAudio(): Promise<boolean> {
  const bag = getBag();
  if (!bag) return false;
  try {
    if (bag.ctx.state === 'suspended') {
      await bag.ctx.resume();
    }
    return bag.ctx.state === 'running';
  } catch {
    return false;
  }
}

function tone(
  bag: AudioBag,
  frequency: number,
  duration: number,
  type: OscillatorType,
  gainValue: number,
  when = 0
) {
  const { ctx, master } = bag;
  const oscillator = ctx.createOscillator();
  const filter = ctx.createBiquadFilter();
  const gain = ctx.createGain();
  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, ctx.currentTime + when);
  filter.type = 'lowpass';
  filter.frequency.value = Math.max(900, frequency * 2.4);
  gain.gain.setValueAtTime(Math.max(0.0001, gainValue), ctx.currentTime + when);
  gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + when + duration);
  oscillator.connect(filter);
  filter.connect(gain);
  gain.connect(master);
  oscillator.start(ctx.currentTime + when);
  oscillator.stop(ctx.currentTime + when + duration + 0.02);
}

function noiseWhoosh(bag: AudioBag, duration: number, gainValue: number) {
  const { ctx, master } = bag;
  const length = Math.max(1, Math.floor(ctx.sampleRate * duration));
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i += 1) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / length);
  }
  const source = ctx.createBufferSource();
  const filter = ctx.createBiquadFilter();
  const gain = ctx.createGain();
  source.buffer = buffer;
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(420, ctx.currentTime);
  filter.frequency.exponentialRampToValueAtTime(1400, ctx.currentTime + duration);
  gain.gain.setValueAtTime(gainValue, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration);
  source.connect(filter);
  filter.connect(gain);
  gain.connect(master);
  source.start();
}

function playSound(event: JuiceEvent) {
  const bag = getBag();
  if (!bag) return;
  if (bag.ctx.state === 'suspended') {
    void bag.ctx.resume();
  }
  switch (event) {
    case 'click':
      tone(bag, 880, 0.045, 'square', 0.035);
      break;
    case 'spin':
      noiseWhoosh(bag, 0.28, 0.05);
      tone(bag, 140, 0.2, 'sawtooth', 0.04);
      break;
    case 'tick':
      tone(bag, 190 + Math.random() * 70, 0.045, 'square', 0.028);
      break;
    case 'cash':
      tone(bag, 523.25, 0.12, 'triangle', 0.07);
      tone(bag, 659.25, 0.14, 'triangle', 0.06, 0.07);
      tone(bag, 783.99, 0.18, 'triangle', 0.055, 0.14);
      tone(bag, 1046.5, 0.22, 'sine', 0.04, 0.22);
      break;
    case 'hit':
      tone(bag, 740, 0.09, 'triangle', 0.07);
      tone(bag, 988, 0.14, 'triangle', 0.055, 0.07);
      tone(bag, 1174, 0.16, 'sine', 0.035, 0.14);
      break;
    case 'miss':
      tone(bag, 196, 0.16, 'sawtooth', 0.04);
      tone(bag, 147, 0.2, 'triangle', 0.03, 0.08);
      break;
    case 'bankrupt':
      tone(bag, 180, 0.22, 'sawtooth', 0.07);
      tone(bag, 110, 0.32, 'square', 0.05, 0.1);
      tone(bag, 70, 0.4, 'sine', 0.045, 0.2);
      break;
    case 'lose':
      tone(bag, 330, 0.12, 'triangle', 0.045);
      tone(bag, 247, 0.18, 'triangle', 0.04, 0.1);
      break;
    case 'solve':
      [523.25, 659.25, 783.99, 1046.5, 1318.5].forEach((frequency, index) => {
        tone(bag, frequency, 0.22, 'triangle', 0.06, index * 0.08);
      });
      break;
    case 'win':
      [392, 523.25, 659.25, 783.99, 1046.5, 1318.5].forEach((frequency, index) => {
        tone(bag, frequency, 0.26, 'triangle', 0.065, index * 0.09);
      });
      break;
    default:
      break;
  }
}

function haptic(event: JuiceEvent) {
  if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') {
    return;
  }
  const patterns: Partial<Record<JuiceEvent, number | number[]>> = {
    click: 8,
    spin: [12, 28, 12, 28, 18],
    cash: [18, 30, 24, 20, 40],
    hit: [16, 18, 22],
    miss: [28, 36, 28],
    bankrupt: [70, 40, 80, 40, 120],
    lose: 36,
    solve: [18, 24, 18, 24, 36, 20, 50],
    win: [22, 30, 22, 30, 44, 24, 70],
  };
  const pattern = patterns[event];
  if (pattern) {
    try {
      navigator.vibrate(pattern);
    } catch {
      // ignore
    }
  }
}

export const JuiceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [muted, setMuted] = useState(() => {
    try {
      return window.localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [audioReady, setAudioReady] = useState(false);

  useEffect(() => {
    applyMuteToMaster(muted);
    try {
      window.localStorage.setItem(STORAGE_KEY, muted ? '1' : '0');
    } catch {
      // ignore
    }
  }, [muted]);

  useEffect(() => {
    const arm = () => {
      void unlockAudio().then((ready) => {
        if (ready) setAudioReady(true);
      });
    };
    window.addEventListener('pointerdown', arm, { passive: true });
    window.addEventListener('keydown', arm);
    return () => {
      window.removeEventListener('pointerdown', arm);
      window.removeEventListener('keydown', arm);
    };
  }, []);

  const toggleMuted = useCallback(() => {
    setMuted((value) => {
      const next = !value;
      applyMuteToMaster(next);
      if (value) {
        void unlockAudio().then((ready) => {
          if (ready) setAudioReady(true);
          try {
            playSound('click');
          } catch {
            // keep mute working even if a tone fails
          }
        });
      }
      return next;
    });
  }, []);

  const play = useCallback(
    (event: JuiceEvent) => {
      if (muted) return;
      void unlockAudio().then((ready) => {
        if (ready) setAudioReady(true);
      });
      try {
        haptic(event);
      } catch {
        // ignore
      }
      try {
        playSound(event);
      } catch {
        // never let a sound glitch break a tap
      }
    },
    [muted]
  );

  const value = useMemo(
    () => ({ muted, audioReady, toggleMuted, play }),
    [muted, audioReady, toggleMuted, play]
  );

  return <JuiceContext.Provider value={value}>{children}</JuiceContext.Provider>;
};

export const useJuice = () => useContext(JuiceContext);
